import { base64Decode } from './base64';
import { getCanonicalOrganizationKey } from './business-route';
import { isIframeLoaded } from './isIframe';

const ALLOWED_ROOT_DOMAINS: Record<string, true> = {
  'dauyan.cn': true,
  'dauyan.net': true,
  'dauyan.com': true,
};

export const isTrustedRedirectUrl = (value: string, sourceHostname = window.location.hostname): boolean => {
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
    const labels = hostname.split('.');
    const rootDomain = labels.slice(-2).join('.');

    if ((sourceHostname === 'localhost' || sourceHostname === '127.0.0.1')
      && (hostname === 'localhost' || hostname === '127.0.0.1')) {
      return url.protocol === 'http:'
        && url.port === '3000'
        && !url.username
        && !url.password;
    }

    return url.protocol === 'https:'
      && !url.username
      && !url.password
      && (!url.port || url.port === '443')
      && labels.length >= 2
      && labels.length <= 4
      && labels.every(Boolean)
      && Boolean(ALLOWED_ROOT_DOMAINS[rootDomain]);
  } catch {
    return false;
  }
};

/**
 * Keep shared assessment links on the code used by their customized login
 * entry. Only the trusted learner application owns this hash route; all other
 * redirects must remain byte-for-byte unchanged.
 */
export const normalizeAssessmentRedirectUri = (redirectUri: string): string => {
  const url = new URL(redirectUri);
  if (url.hostname.toLowerCase() !== 'ielts.dauyan.com' || !url.hash.startsWith('#/')) {
    return redirectUri;
  }

  const route = new URL(url.hash.slice(1), url.origin);
  if (route.pathname !== '/adaptive-assessment') {
    return redirectUri;
  }

  const orgKey = route.searchParams.get('org');
  if (!orgKey) {
    return redirectUri;
  }
  const canonicalOrgKey = getCanonicalOrganizationKey(orgKey);
  if (canonicalOrgKey === orgKey) {
    return redirectUri;
  }

  route.searchParams.set('org', canonicalOrgKey);
  url.hash = `${route.pathname}${route.search}`;
  return url.toString();
};

export const getTrustedRedirectUri = (search = window.location.search): string | null => {
  const encodedUri = new URLSearchParams(search).get('redirect_uri');
  if (!encodedUri) {
    return null;
  }

  try {
    const redirectUri = base64Decode(encodedUri);
    return isTrustedRedirectUrl(redirectUri) ? normalizeAssessmentRedirectUri(redirectUri) : null;
  } catch {
    return null;
  }
};

export const postAuthSuccessToParent = (token: string, redirectUri: string | null): boolean => {
  if (!isIframeLoaded() || !redirectUri) {
    return false;
  }

  window.parent.postMessage({
    type: 'dauyanAuthStatus',
    data: {
      token,
      status: 'success',
    },
  }, new URL(redirectUri).origin);
  return true;
};
